import { useCallback, useEffect, useRef } from 'react'
import {
  deleteTemplate,
  getFileV2,
  getTemplates,
  listAllFilesV2,
  listFilesByFilesIds,
  saveTemplate,
  updateTemplate,
  updateUserSettings,
} from '../api'
import { USER_SETTINGS } from '../constant'
import { addMultipleReferences } from '../slice/referenceSlice'
import {
  DialogArg,
  DialogArgMessage,
  ExtractlyDialogType,
  ExtractlyPlusFile,
  MetricName,
  MetricNamespace,
  RectangleWithFilePage,
  SearchMode,
  Template,
  TemplateDrawing,
  TemplateDrawingRect,
  TemplateWorkerJobType,
  UserSettings,
} from '../types'
import { getDialogSize } from '../utils/common'
import {
  isDialogMessageArg,
  isStringArray,
  isUserSetting,
} from '../utils/guards'
import { addMetric } from '../utils/metrics'
import { exportTemplateDataToWorksheet, pdfToBase64 } from '../workbook'
import { stringifyError } from '../utils/json'
import { groupBy } from 'lodash'
import { useAppDispatch } from '../dispatch'
import mittEmitter from '../utils/mitt'

let dialog: Office.AsyncResult<Office.Dialog> | null = null

const useDialog = (dispatch: ReturnType<typeof useAppDispatch>) => {
  const templateWorker = useRef<Worker | null>(null)
  const dispatchRef = useRef(dispatch)

  const processDialogMsg = useCallback(async (arg: DialogArg) => {
    if (isDialogMessageArg(arg)) {
      const message: DialogArgMessage = JSON.parse(arg.message)
      if (
        !dialog ||
        dialog.status !== Office.AsyncResultStatus.Succeeded ||
        !process.env.REACT_APP_DIALOG_APP
      )
        return
      switch (message.type) {
        case ExtractlyDialogType.DIALOG_INIT_TEMPLATE: {
          const data = message.data as {
            template: Template
            files: string[]
          }
          listFilesByFilesIds(data.files)
            .then((files) => {
              const filtered = files.filter((file) =>
                data.files.includes(file.fileId)
              )
              const mapped = filtered.map((file) => getFileV2(file.fileId))
              return Promise.all(mapped)
            })
            .then((files) => {
              if (templateWorker.current) {
                templateWorker.current.postMessage({
                  files: JSON.stringify(files),
                  template: JSON.stringify(data.template),
                  type: TemplateWorkerJobType.INIT_TEMPLATE,
                })
              }
            })
          break
        }
        case ExtractlyDialogType.DIALOG_CHANGE_ANCHOR: {
          const data = message.data as {
            batchId: string
            fileIds: string[]
            originalAnchor: TemplateDrawingRect
            rect: RectangleWithFilePage
            searchingFileId: string
            value: TemplateDrawingRect
            name: string | undefined
            searchMode: SearchMode
          }
          listFilesByFilesIds(data.fileIds)
            .then((files) => {
              const filtered = files.filter((file) =>
                data.fileIds.includes(file.fileId)
              )
              const mapped = filtered.map((file) => getFileV2(file.fileId))
              return Promise.all(mapped)
            })
            .then((files) => {
              if (templateWorker.current) {
                templateWorker.current.postMessage({
                  files: JSON.stringify(files),
                  batchId: data.batchId,
                  originalAnchor: JSON.stringify(data.originalAnchor),
                  rect: JSON.stringify(data.rect),
                  searchingFileId: data.searchingFileId,
                  value: JSON.stringify(data.value),
                  name: data.name,
                  type: TemplateWorkerJobType.CHANGE_ANCHOR,
                  searchMode: data.searchMode,
                })
              }
            })
          break
        }
        case ExtractlyDialogType.DIALOG_UPDATE_USER_SETTINGS: {
          const userSettings = message.data as UserSettings
          let data: {
            data: null | UserSettings
            type: ExtractlyDialogType
          } = {
            data: null,
            type: ExtractlyDialogType.DIALOG_RETURN_UPDATE_USER_SETTINGS,
          }
          if (isUserSetting(userSettings)) {
            try {
              const updated = await updateUserSettings(userSettings)
              data.data = updated.data.data
              localStorage.setItem(USER_SETTINGS, JSON.stringify(data.data))
            } catch (err) {
              console.error(
                'ExtractlyDialogType.DIALOG_UPDATE_USER_SETTINGS error:',
                err
              )
            } finally {
              dialog.value.messageChild(JSON.stringify(data), {
                targetOrigin: process.env.REACT_APP_DIALOG_APP,
              })
            }
          } else {
            const userSettings = localStorage.getItem(USER_SETTINGS)
            if (userSettings) {
              const settings: UserSettings = JSON.parse(userSettings)
              data.data = settings
            }
            dialog.value.messageChild(JSON.stringify(data), {
              targetOrigin: process.env.REACT_APP_DIALOG_APP,
            })
          }
          break
        }
        case ExtractlyDialogType.DIALOG_FETCH_USER_SETTINGS: {
          const userSettings = localStorage.getItem(USER_SETTINGS)
          if (
            userSettings !== null &&
            isUserSetting(JSON.parse(userSettings))
          ) {
            const data = {
              data: userSettings,
              type: ExtractlyDialogType.DIALOG_RETURN_USER_SETTINGS,
            }
            dialog.value.messageChild(JSON.stringify(data), {
              targetOrigin: process.env.REACT_APP_DIALOG_APP,
            })
          } else {
            const data = {
              data: null,
              type: ExtractlyDialogType.DIALOG_RETURN_USER_SETTINGS,
            }
            dialog.value.messageChild(JSON.stringify(data), {
              targetOrigin: process.env.REACT_APP_DIALOG_APP,
            })
          }
          break
        }
        case ExtractlyDialogType.DIALOG_RESIZE_BOX: {
          const { fileIds, rect, batchId, name, searchingFileId } =
            message.data as {
              fileIds: string[]
              rect: RectangleWithFilePage
              batchId: string
              name: string | undefined
              searchingFileId: string
            }
          listFilesByFilesIds(fileIds)
            .then((files) => {
              const filtered = files.filter((file) =>
                fileIds.includes(file.fileId)
              )
              const mapped = filtered.map((file) => getFileV2(file.fileId))
              return Promise.all(mapped)
            })
            .then((promise) => {
              if (templateWorker.current) {
                templateWorker.current.postMessage({
                  files: JSON.stringify(promise),
                  rect: JSON.stringify(rect),
                  resize: true,
                  batchId,
                  name,
                  searchingFileId,
                  type: TemplateWorkerJobType.SEARCH,
                })
              }
            })
            .catch((err) =>
              console.error(
                'ExtractlyDialogType.DIALOG_SEARCH_FILES error:',
                err
              )
            )
          break
        }
        case ExtractlyDialogType.DIALOG_UPDATE_TEMPLATE: {
          const d = message.data as {
            template: Template
            drawings: TemplateDrawing[]
          }
          updateTemplate(d.template)
            .then(async () => {
              const elems = await exportTemplateDataToWorksheet(d.drawings)
              if (elems) await dispatchRef.current(addMultipleReferences(elems))
              await addMetric({
                namespace: MetricNamespace.UPDATE_TEMPLATE,
                metric_value: 1,
                metric_name: MetricName.COUNT,
              })
            })
            .catch((err) =>
              console.log(
                `ExtractlyDialogType.DIALOG_UPDATE_TEMPLATE error=${err}`
              )
            )
            .finally(() => dialog?.value.close())
          break
        }
        case ExtractlyDialogType.DIALOG_DELETE_TEMPLATE: {
          deleteTemplate(message.data as string)
            .then(async () => {
              if (!dialog || !process.env.REACT_APP_DIALOG_APP) return
              const res = await getTemplates()
              const data = {
                data: res.data.data,
                type: ExtractlyDialogType.DIALOG_RETURN_TEMPLATES,
              }
              dialog.value.messageChild(JSON.stringify(data), {
                targetOrigin: process.env.REACT_APP_DIALOG_APP,
              })
            })
            .catch((err) => {
              console.error(
                `ExtractlyDialogType.DIALOG_DELETE_TEMPLATE error=${err}`
              )
            })
          break
        }
        case ExtractlyDialogType.DIALOG_FETCH_TEMPLATES: {
          getTemplates()
            .then((res) => {
              const data = {
                data: res.data.data,
                type: ExtractlyDialogType.DIALOG_RETURN_TEMPLATES,
              }
              if (dialog && process.env.REACT_APP_DIALOG_APP) {
                dialog.value.messageChild(JSON.stringify(data), {
                  targetOrigin: process.env.REACT_APP_DIALOG_APP,
                })
              }
            })
            .catch((err) => {
              console.error(
                'ExtractlyDialogType.DIALOG_FETCH_TEMPLATES error:',
                err
              )
            })
          break
        }
        case ExtractlyDialogType.DIALOG_SAVE_AS_TEMPLATE: {
          const { drawings, template } = message.data as {
            template: Template
            drawings: TemplateDrawing[]
          }
          await saveTemplate(template)
            .then(async () => {
              const elems = await exportTemplateDataToWorksheet(drawings)
              if (elems) await dispatchRef.current(addMultipleReferences(elems))
              await addMetric({
                namespace: MetricNamespace.SAVE_TEMPLATE,
                metric_value: 1,
                metric_name: MetricName.COUNT,
              })
            })
            .catch((err) => {
              console.error(
                'ExtractlyDialogType.DIALOG_SAVE_AS_TEMPLATE error:',
                err
              )
            })
            .finally(() => {
              if (dialog) dialog.value.close()
            })
          break
        }
        case ExtractlyDialogType.DIALOG_EXPORT_TO_WORKSHEET: {
          dialog.value.close()
          exportTemplateDataToWorksheet(message.data as TemplateDrawing[])
            .then(async (elems) => {
              if (elems) await dispatchRef.current(addMultipleReferences(elems))
              await addMetric({
                namespace: MetricNamespace.EXPORT_AUTOMATED_EXTRACTION,
                metric_value: 1,
                metric_name: MetricName.COUNT,
              })
            })
            .catch((err) =>
              console.error(
                'ExtractlyDialogType.DIALOG_EXPORT_TO_WORKSHEET error:',
                err
              )
            )
          break
        }
        case ExtractlyDialogType.DIALOG_SEARCH_FILES: {
          const { fileIds, rect, batchId, name, searchingFileId, searchMode } =
            message.data as {
              fileIds: string[]
              rect: RectangleWithFilePage
              batchId: string
              name: string | undefined
              searchingFileId: string
              searchMode: SearchMode
            }
          listFilesByFilesIds(fileIds)
            .then((files) => {
              const filtered = files.filter((file) =>
                fileIds.includes(file.fileId)
              )
              const mapped = filtered.map((file) => getFileV2(file.fileId))
              return Promise.all(mapped)
            })
            .then((promise) => {
              if (templateWorker.current) {
                templateWorker.current.postMessage({
                  files: JSON.stringify(promise),
                  rect: JSON.stringify(rect),
                  batchId,
                  name,
                  searchingFileId,
                  searchMode,
                  type: TemplateWorkerJobType.SEARCH,
                })
              }
            })
            .catch((err) =>
              console.error(
                'ExtractlyDialogType.DIALOG_SEARCH_FILES error:',
                err
              )
            )
          break
        }
        case ExtractlyDialogType.DIALOG_FETCH_FILE: {
          getFileV2(message.data as string)
            .then(async (result) => {
              const serialized = await pdfToBase64(result.pdf)
              return [result, serialized] as [ExtractlyPlusFile, string]
            })
            .then(([result, serialized]) => {
              const data = {
                data: { ...result, pdf: serialized },
                type: ExtractlyDialogType.DIALOG_RETURN_FETCH_FILE_RESULT,
              }
              if (dialog && process.env.REACT_APP_DIALOG_APP) {
                dialog.value.messageChild(JSON.stringify(data), {
                  targetOrigin: process.env.REACT_APP_DIALOG_APP,
                })
              }
            })
            .catch((err) =>
              console.error('ExtractlyDialogType.DIALOG_FETCH_FILE error:', err)
            )
          break
        }
        case ExtractlyDialogType.DIALOG_FETCHING_FILES: {
          listAllFilesV2()
            .then((files) => {
              const data = {
                data: files,
                type: ExtractlyDialogType.DIALOG_FILES,
              }
              if (dialog && process.env.REACT_APP_DIALOG_APP) {
                dialog.value.messageChild(JSON.stringify(data), {
                  targetOrigin: process.env.REACT_APP_DIALOG_APP,
                })
              }
            })
            .catch((err) =>
              console.error(
                'ExtractlyDialogType.DIALOG_FETCHING_FILES error:',
                err
              )
            )
          break
        }
        case ExtractlyDialogType.DIALOG_FETCH_FILES_WITH_IDS: {
          if (!isStringArray(message.data)) break
          const fileIds = message.data
          listAllFilesV2()
            .then((files) => {
              const data = {
                data: files.filter((file) => fileIds.includes(file.fileId)),
                type: ExtractlyDialogType.DIALOG_RETURN_FOR_FETCH_FILES_WITH_IDS,
              }
              if (dialog && process.env.REACT_APP_DIALOG_APP) {
                dialog.value.messageChild(JSON.stringify(data), {
                  targetOrigin: process.env.REACT_APP_DIALOG_APP,
                })
              }
            })
            .catch((err) =>
              console.error(
                'ExtractlyDialogType.DIALOG_FETCH_FILES_WITH_IDS error:',
                err
              )
            )
          break
        }
        default: {
          console.log('Dialog is ready')
        }
      }
    }
  }, [])

  const openDialog = useCallback(
    (suffix: string = '', height: number = 1024, width: number = 1366) => {
      const dialogUrl = process.env.REACT_APP_DIALOG_APP
      if (!dialogUrl) return
      // console.log('window.screen.width:', window.screen.width)
      Office.context.ui.displayDialogAsync(
        suffix ? `${dialogUrl}/${suffix}` : dialogUrl,
        { ...getDialogSize(height, width) },
        (result) => {
          console.log('Dialog result:', result)
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            dialog = result
            result.value.addEventHandler(
              Office.EventType.DialogMessageReceived,
              processDialogMsg
            )
          } else if (result.status === Office.AsyncResultStatus.Failed) {
            if (
              result.error.message ===
                'The operation failed because this add-in already has an active dialog.' &&
              dialog
            ) {
              dialog.value.close()
            }
          }
        }
      )
    },
    [processDialogMsg]
  )

  useEffect(() => {
    const worker = new Worker(
      new URL('../workers/template_worker.ts', import.meta.url)
    )

    templateWorker.current = worker

    worker.onmessage = async (message) => {
      const {
        success,
        error,
        batchId,
        name,
        resize,
        labelRects,
        valueRects,
        type,
        searchMode,
      } = message.data
      if (!success) {
        const data = {
          data: {
            error: stringifyError(error),
          },
          type: '',
        }
        if (type === TemplateWorkerJobType.CHANGE_ANCHOR) {
          data.type = ExtractlyDialogType.DIALOG_RETURN_CHANGE_ANCHOR
        } else if (type === TemplateWorkerJobType.SEARCH) {
          data.type = ExtractlyDialogType.DIALOG_RETURN_SEARCH_FILES_RESULT
        } else {
          data.type = ExtractlyDialogType.DIALOG_RETURN_INIT_TEMPLATE
        }
        if (!dialog || !process.env.REACT_APP_DIALOG_APP) return
        dialog.value.messageChild(JSON.stringify(data), {
          targetOrigin: process.env.REACT_APP_DIALOG_APP,
        })
      } else if (type === TemplateWorkerJobType.INIT_TEMPLATE) {
        const parsedLabelRects: TemplateDrawingRect[] = JSON.parse(labelRects)
        const parsedValueRects: TemplateDrawingRect[] = JSON.parse(valueRects)
        const groupedLabels = groupBy(parsedLabelRects, (rect) => rect.batchId)
        const groupedValues = groupBy(parsedValueRects, (rect) => rect.batchId)
        if (!dialog || !process.env.REACT_APP_DIALOG_APP) return
        const data: {
          batchId: string | undefined
          name: string | undefined
          labelRects: string
          valueRects: string
        }[] = []
        for (const labelRects of Object.values(groupedLabels)) {
          if (labelRects.length === 0) {
            continue
          }
          const valueRects = labelRects[0].batchId
            ? groupedValues[labelRects[0].batchId]
            : []
          if (
            !valueRects.length ||
            valueRects.length !== labelRects.length ||
            valueRects[0].name !== labelRects[0].name
          ) {
            continue
          }
          data.push({
            batchId: labelRects[0].batchId,
            name: labelRects[0].name,
            labelRects: JSON.stringify(labelRects),
            valueRects: JSON.stringify(valueRects),
          })

          // const data = {
          //   data: {
          //     batchId: labelRects[0].batchId,
          //     name,
          //     labelRects: JSON.stringify(labelRects),
          //     valueRects: JSON.stringify(valueRects),
          //     searchMode,
          //   },
          //   type: ExtractlyDialogType.DIALOG_RETURN_SEARCH_FILES_RESULT,
          // }
          // dialog.value.messageChild(JSON.stringify(data), {
          //   targetOrigin: process.env.REACT_APP_DIALOG_APP,
          // })
        }
        const result = {
          data: {
            data,
            searchMode,
          },
          type: ExtractlyDialogType.DIALOG_RETURN_INIT_TEMPLATE,
        }
        dialog.value.messageChild(JSON.stringify(result), {
          targetOrigin: process.env.REACT_APP_DIALOG_APP,
        })
      } else if (type === TemplateWorkerJobType.CHANGE_ANCHOR) {
        // Handle change anchor logic
        const data = {
          data: {
            batchId,
            name,
            labelRects,
            valueRects,
            searchMode,
          },
          type: ExtractlyDialogType.DIALOG_RETURN_CHANGE_ANCHOR,
        }

        if (!dialog || !process.env.REACT_APP_DIALOG_APP) return
        dialog.value.messageChild(JSON.stringify(data), {
          targetOrigin: process.env.REACT_APP_DIALOG_APP,
        })
      } else {
        if (!dialog || !process.env.REACT_APP_DIALOG_APP) return
        if (resize) {
          const data = {
            data: { batchId, name, labelRects, valueRects, searchMode },
            type: ExtractlyDialogType.DIALOG_RETURN_RESIZE_BOX,
            batchId,
          }
          dialog.value.messageChild(JSON.stringify(data), {
            targetOrigin: process.env.REACT_APP_DIALOG_APP,
          })
        } else {
          const data = {
            data: {
              batchId,
              name,
              labelRects,
              valueRects,
              searchMode,
            },
            type: ExtractlyDialogType.DIALOG_RETURN_SEARCH_FILES_RESULT,
          }
          dialog.value.messageChild(JSON.stringify(data), {
            targetOrigin: process.env.REACT_APP_DIALOG_APP,
          })
        }
      }
    }

    return () => worker.terminate()
  }, [])

  useEffect(() => {
    const handler = () => {
      openDialog()
    }

    mittEmitter.on('OPEN_DIALOG', handler)

    return () => {
      mittEmitter.off('OPEN_DIALOG', handler)
    }
  }, [openDialog])

  useEffect(() => {
    const handler = () => {
      openDialog('settings', 225, 600)
    }

    mittEmitter.on('OPEN_USER_SETTINGS', handler)

    return () => {
      mittEmitter.off('OPEN_USER_SETTINGS', handler)
    }
  }, [openDialog])
}

export default useDialog
